import { DateTime } from "luxon";
import type { FC, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import rocketLaunchAnimation from "../../../assets/animations/rocket-launch.json";
import { UnlockBurst } from "../../../components/UIToolkit/Animations/UnlockBurst";
import type { SystemChipItem } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { FilterFieldList } from "../../../components/UIToolkit/Filters/FilterFieldList";
import type { FilterSection } from "../../../components/UIToolkit/Filters/filterFields";
import { ShiftHoursDisplayAndPicker } from "../../../components/UIToolkit/Inputs/ShiftHoursDisplayAndPicker";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { DropdownItem } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import type { AdminRole, Nurse } from "../../../utils/models";

/**
 * Every filter value that can be "active", declared once so this rail's own section badges and
 * `GroupedActivityLog.tsx`'s collapsed-rail total can never disagree about what counts. `isSupport`
 * rides along because Dev Mode's count is clamped on it — see `countActiveGroupedActivityLogFilters`.
 */
export type GroupedActivityLogActiveFilters = {
	performedBy: string[];
	performedForNurse: SystemAutoCompleteOption<Nurse> | null;
	/** The changer's roles in this department. Multi-select, unlike the Raw tab's single-select. */
	adminRoles: AdminRole[];
	selectedChannels: SystemChipItem<string>[];
	actionNames: string[];
	/** Specific Shift filter — a single date + start/end time (composed client-side into one absolute
	 * `shift_window_start`/`shift_window_end` before the query fires — see `GroupedActivityLog.tsx`'s
	 * `composeShiftWindow`) plus an independent job multi-select. All three are optional and independent
	 * of one another (date alone, date+times, +job, or job alone). */
	shiftDate: DateTime | undefined;
	shiftStartTime: DateTime | null;
	shiftEndTime: DateTime | null;
	shiftJobIds: string[];
	/** Dev Mode is support-only (this app's existing support-gating convention — see `RawPunchesChip`) —
	 * gates ONLY the Dev Mode switch below; the Punches switch stays visible to everyone, unchanged. */
	isSupport: boolean;
	devMode: boolean;
	showPunches: boolean;
};

/** Per-section active counts plus their sum: each section badges its own number, the rail badges `total`. */
export type GroupedActivityLogFilterCounts = {
	people: number;
	action: number;
	channel: number;
	shift: number;
	display: number;
	total: number;
};

/**
 * Per-section active count, so a collapsed section still says how many filters it is applying — a
 * filtered table whose cause is folded away is the most confusing state a rail has.
 *
 * The date range is deliberately excluded: it always holds a value, so counting it would badge every
 * section on first paint and the number would stop meaning "you narrowed something" — which is also
 * why it is not part of "Clear all" (see `GroupedActivityLog.tsx`'s `handleClearAllFilters`).
 */
export const countActiveGroupedActivityLogFilters = (values: GroupedActivityLogActiveFilters): GroupedActivityLogFilterCounts => {
	const people = values.performedBy.length + (values.performedForNurse ? 1 : 0) + values.adminRoles.length;
	const action = values.actionNames.length;
	const channel = values.selectedChannels.length;
	const shift = values.shiftJobIds.length + (values.shiftDate ? 1 : 0) + (values.shiftStartTime || values.shiftEndTime ? 1 : 0);
	// `devMode` is the raw localStorage value, clamped here on the same `isSupport` that hides the switch
	// itself: a non-support user with a stale stored `true` would otherwise get a "Display · 1" badge
	// pointing at a control that is never rendered, and no way to clear it.
	const display = (values.showPunches ? 1 : 0) + (values.isSupport && values.devMode ? 1 : 0);
	return { people, action, channel, shift, display, total: people + action + channel + shift + display };
};

type Props = GroupedActivityLogActiveFilters & {
	isLoading: boolean;
	actionTimeStart: DateTime | undefined;
	actionTimeEnd: DateTime | undefined;
	onActionTimeStartChange: (date: DateTime) => void;
	onActionTimeEndChange: (date: DateTime) => void;
	performedByOptions: DropdownItem<string>[];
	performedByOptionsLoading: boolean;
	onPerformedByChange: (values: string[]) => void;
	nurseOptions: SystemAutoCompleteOption<Nurse>[];
	onPerformedForChange: (event: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => void;
	adminRoleItems: DropdownItem<string>[];
	onAdminRolesChange: (values: AdminRole[]) => void;
	channelItems: SystemChipItem<string>[];
	onChannelChange: (item: SystemChipItem<string>, selected: boolean) => void;
	actionNameItems: DropdownItem<string>[];
	onActionNamesChange: (values: string[]) => void;
	onShiftDateChange: (date: DateTime | undefined) => void;
	onShiftStartTimeChange: (value: DateTime | null) => void;
	onShiftEndTimeChange: (value: DateTime | null) => void;
	shiftJobOptions: DropdownItem<string>[];
	onShiftJobIdsChange: (values: string[]) => void;
	/** "Exact times" — ON by default, switches the Specific Shift filter's window match from overlap to
	 * exact equality on the backend. Disabled (with a tooltip explaining why) until the user has actually
	 * set BOTH `shiftStartTime`/`shiftEndTime` — see `GroupedActivityLog.tsx`'s `shiftExactTimesApplied`
	 * for the matching clamp on the value actually sent. */
	exactShiftTimes: boolean;
	onExactShiftTimesChange: (checked: boolean) => void;
	onDevModeChange: (checked: boolean) => void;
	onShowPunchesChange: (checked: boolean) => void;
};

/**
 * The Grouped Activity Log's filter rail, declared as `FilterFieldList` config.
 *
 * Rendered inside `GroupedActivityLog.tsx`'s `FilterSidebar` (a fixed-width vertical panel, not the
 * grid's own toolbar), as six titled collapsible sections — the `sections` config below is the
 * authoritative list and render order.
 *
 * The loading placeholder is derived from this same config rather than hand-written beside it, which
 * is what retired the parallel skeleton component this file used to carry — that copy had already
 * drifted once (its Shift Jobs field and the Specific Shift box were in the wrong order) and it had
 * to re-check `isSupport` by hand or briefly show a placeholder for a control a non-support user
 * would never get.
 */
export const GroupedActivityLogFilters: FC<Props> = ({
	isLoading,
	actionTimeStart,
	actionTimeEnd,
	onActionTimeStartChange,
	onActionTimeEndChange,
	performedByOptions,
	performedByOptionsLoading,
	performedBy,
	onPerformedByChange,
	nurseOptions,
	performedForNurse,
	onPerformedForChange,
	adminRoles,
	adminRoleItems,
	onAdminRolesChange,
	channelItems,
	selectedChannels,
	onChannelChange,
	actionNameItems,
	actionNames,
	onActionNamesChange,
	shiftDate,
	onShiftDateChange,
	shiftStartTime,
	onShiftStartTimeChange,
	shiftEndTime,
	onShiftEndTimeChange,
	shiftJobOptions,
	shiftJobIds,
	onShiftJobIdsChange,
	exactShiftTimes,
	onExactShiftTimesChange,
	isSupport,
	devMode,
	onDevModeChange,
	showPunches,
	onShowPunchesChange,
}) => {
	const { t } = useTranslation();

	const counts = countActiveGroupedActivityLogFilters({
		performedBy,
		performedForNurse,
		adminRoles,
		selectedChannels,
		actionNames,
		shiftDate,
		shiftStartTime,
		shiftEndTime,
		shiftJobIds,
		isSupport,
		devMode,
		showPunches,
	});

	// The backend silently excludes deferred (large bulk-operation) groups from the response whenever one
	// of these filters is active — they each need per-group data that's expensive to compute for a huge
	// group, and computing it would defeat the point of deferring in the first place. `actionNames` is
	// deliberately NOT here: it still matches deferred groups normally, so it's the safe alternative this
	// hint points users at. Mirrors exactly which filters `shiftWindow`/`shiftJobIds` in
	// `GroupedActivityLog.tsx`'s own `filters` object represent — `shiftDate` alone (rather than the
	// composed window) is enough to know a Specific Shift filter is active, same as `composeShiftWindow`'s
	// own "no date, no window" gate.
	const hasDeferredExcludingFilter =
		performedForNurse !== null || performedBy.length > 0 || adminRoles.length > 0 || selectedChannels.length > 0 || !!shiftDate || shiftJobIds.length > 0;

	/**
	 * Six titled sections rather than one flat run of controls.
	 *
	 * Not memoized: `FilterFieldList` isn't memoized either, so a stable reference saves no render —
	 * and a dependency array covering every value below is pure bug surface (one stale entry = a dead
	 * handler). The hand-written JSX this replaced rebuilt the same nodes every render for the same reason.
	 *
	 * The rail used to be nine unlabeled inputs at a uniform gap — four of them identical empty
	 * selects — so nothing told you what any control belonged to or which ones were doing anything.
	 * Titled, collapsible, divider-separated sections are the same language the Team rail already
	 * speaks (`FacetFilterSection`), which is why both now share `FilterSectionHeader`.
	 *
	 * The Specific Shift box is gone with them: a drawn border was doing the grouping a section title
	 * now does, and it drew a subordinate group more strongly than the panel containing it.
	 */
	const sections: FilterSection[] = [
		{
			// Untitled and gated rather than conditionally spliced in, so the hint lives in the same
			// declarative config as everything else in the rail.
			id: "deferred-hint",
			hidden: !hasDeferredExcludingFilter,
			fields: [
				{
					kind: "custom",
					id: "deferred-hint",
					render: () => (
						<InlineHint tone="info" dataTestId="grouped-activity-log-deferred-filter-hint">
							{t("activityLog.grouped.filter.deferredHiddenHint")}
						</InlineHint>
					),
				},
			],
		},
		{
			id: "date-range",
			title: t("activityLog.grouped.filter.dateRange"),
			collapsible: true,
			fields: [
				{
					kind: "dateRange",
					id: "action-time",
					startDate: actionTimeStart,
					endDate: actionTimeEnd,
					onStartDateChange: onActionTimeStartChange,
					onEndDateChange: onActionTimeEndChange,
				},
			],
		},
		{
			id: "people",
			title: t("activityLog.grouped.filter.section.people"),
			collapsible: true,
			activeCount: counts.people,
			fields: [
				{
					kind: "select",
					id: "performed-by",
					multiple: true,
					label: t("activityLog.grouped.filter.performedBy"),
					items: performedByOptions,
					loading: performedByOptionsLoading,
					value: performedBy,
					onChange: onPerformedByChange,
					dataTestId: "grouped-activity-log-performed-by-filter",
				},
				{
					kind: "staff",
					id: "performed-for",
					label: t("activityLog.grouped.filter.performedFor"),
					nurses: nurseOptions,
					value: performedForNurse,
					onChange: onPerformedForChange,
				},
				{
					kind: "select",
					id: "admin-roles",
					multiple: true,
					label: t("activityLog.grouped.filter.role"),
					items: adminRoleItems,
					value: adminRoles,
					// Items all come from ADMIN_ROLES; the string[] is just the shared field's contract.
					onChange: (values) => onAdminRolesChange(values as AdminRole[]),
					dataTestId: "grouped-activity-log-admin-roles-filter",
				},
			],
		},
		// Action and Channel are separate sections, each holding one control — and that control drops its
		// own label, because the section title already says it. Sharing one "Activity" section forced the
		// chips to carry a second-level uppercase label that read as debris between two fields.
		{
			id: "action",
			title: t("activityLog.grouped.filter.actionNames"),
			collapsible: true,
			activeCount: counts.action,
			fields: [
				{
					kind: "select",
					id: "action-names",
					multiple: true,
					items: actionNameItems,
					value: actionNames,
					onChange: onActionNamesChange,
					// The section title already says "Action", so the trigger states the VALUE instead —
					// repeating the dimension name inside its own section reads as a stutter.
					placeholder: t("filter.value.all"),
					dataTestId: "grouped-activity-log-action-names-filter",
				},
			],
		},
		{
			id: "channel",
			title: t("activityLog.grouped.filter.channel"),
			collapsible: true,
			activeCount: counts.channel,
			fields: [
				{
					kind: "chips",
					id: "channel",
					items: channelItems,
					selected: selectedChannels,
					onChange: onChannelChange,
					dataTestId: "grouped-activity-log-channel-filter",
				},
			],
		},
		{
			id: "shift",
			title: t("activityLog.grouped.filter.section.shift"),
			collapsible: true,
			activeCount: counts.shift,
			fields: [
				{
					kind: "select",
					id: "shift-jobs",
					multiple: true,
					label: t("activityLog.grouped.filter.shiftJobs"),
					items: shiftJobOptions,
					value: shiftJobIds,
					onChange: onShiftJobIdsChange,
					dataTestId: "grouped-activity-log-shift-jobs-filter",
				},
				{
					kind: "date",
					id: "shift-date",
					label: t("activityLog.grouped.filter.shiftDate"),
					value: shiftDate,
					onChange: onShiftDateChange,
					dataTestId: "grouped-activity-log-shift-date-filter",
				},
				{
					kind: "custom",
					id: "shift-hours",
					dataTestId: "grouped-activity-log-shift-hours-filter",
					render: () => (
						<ShiftHoursDisplayAndPicker
							selectedRoleForShift={undefined}
							roleSource="role"
							jobId={shiftJobIds[0]}
							// Gated on shiftDate, not unconditionally enabled: times are meaningless without a
							// date to attach them to, and `composeShiftWindow` already refuses to compose a
							// window (so nothing reaches the API) until a date exists — this keeps the UI from
							// ever letting a user pick times first, matching that same date-first contract.
							enabled={!!shiftDate}
							// The component's own default disabled tooltip ("select a role first") is written
							// for its original role-gated callers — wrong reason here, since we disable it for
							// a missing DATE, not a missing role/nurse.
							disabledTooltip={t("activityLog.grouped.filter.shiftHoursDisabledTooltip")}
							// DEFAULT: ShiftHoursDisplayAndPicker requires concrete values to display (same
							// constraint AddDayOffDialog.tsx hit) — until the user actually sets a time, fall
							// back to the picked date's own start/end of day so the display always shows
							// something, while `shiftStartTime`/`shiftEndTime` stay null underneath (so
							// `composeShiftWindow` still treats an untouched filter as "whole day", not
							// narrowed to whatever this fallback happens to display).
							shiftStart={shiftStartTime ?? shiftDate?.startOf("day") ?? DateTime.now().startOf("day")}
							shiftEnd={shiftEndTime ?? shiftDate?.endOf("day") ?? DateTime.now().endOf("day")}
							setShiftStart={onShiftStartTimeChange}
							setShiftEnd={onShiftEndTimeChange}
							hideLabel
							// Rendered inside the picker itself (its own optional `onClear`, not a separate
							// external button here) — resets both times back to null (not just the display), so
							// `composeShiftWindow` falls back to "whole day" and the disabled Exact Times switch
							// below loses its (already-inert) checked state along with it. Always available —
							// harmless no-op when times are already unset.
							onClear={() => {
								onShiftStartTimeChange(null);
								onShiftEndTimeChange(null);
							}}
						/>
					),
				},
				{
					kind: "switch",
					id: "exact-shift-times",
					label: t("activityLog.grouped.filter.exactShiftTimes"),
					checked: exactShiftTimes,
					onChange: onExactShiftTimesChange,
					disabled: !shiftStartTime || !shiftEndTime,
					tooltip: !shiftStartTime || !shiftEndTime ? t("activityLog.grouped.filter.exactShiftTimesTooltip") : undefined,
					dataTestId: "grouped-activity-log-exact-shift-times-filter",
				},
			],
		},
		{
			id: "display",
			title: t("activityLog.grouped.filter.section.display"),
			collapsible: true,
			activeCount: counts.display,
			fields: [
				{
					kind: "switch",
					id: "punches",
					label: t("activityLog.grouped.filter.punches"),
					checked: showPunches,
					onChange: onShowPunchesChange,
					tooltip: t("activityLog.grouped.filter.punchesTooltip"),
					dataTestId: "grouped-activity-log-punches-filter",
				},
				{
					kind: "switch",
					id: "dev-mode",
					label: t("activityLog.grouped.filter.devMode"),
					checked: devMode,
					onChange: onDevModeChange,
					hidden: !isSupport,
					dataTestId: "grouped-activity-log-dev-mode-filter",
					// Wordless "dev mode" easter egg — a rocket launches on the OFF→ON toggle and lands
					// (same asset, played in reverse) on the ON→OFF toggle. Purely decorative
					// (`aria-hidden`, `pointerEvents: none`), so it can never disturb the switch's hit
					// area or this row's layout.
					adornment: <UnlockBurst active={devMode} animationData={rocketLaunchAnimation} dataTestId="grouped-activity-log-dev-mode-unlock-burst" />,
				},
			],
		},
	];

	return (
		<FilterFieldList
			sections={sections}
			loading={isLoading}
			dividers
			fieldGap={1.5}
			// This rail runs out of filters well before it runs out of column, so the leftover height is
			// better spent separating the sections than left dead at the bottom.
			sectionPadding={1.25}
			dataTestId="grouped-activity-log-filter-section"
		/>
	);
};
